/** @jsxImportSource @emotion/react */
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../../actions/snackbarActions";
import PageTitle from "../../../Utils/smsTitle";
import { SubHeader } from "../../../../Containers/SubHeader";
import MainSnackbar from "../../../Utils/Snackbar/SmsSnackbar";
import ObGynSidebar from "../../Utils/ObGynSidebar";
import ObGynPatientInfo from "../../Utils/ObGynPatientInfo";
import { useANCContact } from "./hooks/useANCContact";
import ANCContactForm from "./ANCContactForm";
import ANCContactTimeline from "./ANCContactTimeline";
import { history } from "../../../../Helpers/history";
import {
    AssessmentContainer,
    PregnancyHeader,
    PregnancyHeaderTop,
    PregnancyTitle,
    PregnancySubtitle,
    PregnancyStatusChip,
    PregnancyStatsRow,
    PregnancyStat,
    PregnancyStatLabel,
    PregnancyStatValue,
    ProgressBarWrapper,
    ProgressBarTrack,
    ProgressBarFill,
    ProgressLabel,
    TrimesterMarkers,
    TrimesterMark,

    NoBookingCard,
    NoBookingIcon,
    NoBookingTitle,
    NoBookingDesc,
    NoBookingButton,
    SkeletonLine,
} from "./ANCClinicalAssessmentStyles";
import { ObGynTabBar, ObGynTabBtn, ObGynTabBadge } from "../../Utils/ObGynTabStyles";

/**
 * ANCClinicalAssessment — Enhanced page-level container for ANC Clinical Assessment
 *
 * Provides:
 *  - Pregnancy progress header with gradient and gestational age visualization
 *  - Tab toggle between "Record Contact" (form) and "Contact History" (timeline)
 *  - Uses ObGynSidebar for consistent navigation
 *  - Displays patient info strip
 *  - No-booking CTA when patient has no active ANC booking
 *  - Loading skeleton while fetching booking data
 *
 * Route: /gyn-and-obs-anc-assessment/:patient_number/:visit_id/:patient_type
 */
const ANCClinicalAssessment = function(props) {
    const actions = props.actions;
    const snackbar = props.snackbar;
    const params = props.match.params;
    const patient_number = params.patient_number;
    const visit_id = params.visit_id;
    const patient_type = params.patient_type;

    /* ─── Current user ─── */
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userObj = !user ? {} : user;
    const token = !userObj.token ? [] : userObj.token;
    const _token = !token[1] ? {} : token[1];
    const doctor_name = (!_token.first_name ? "" : _token.first_name) + " " +
        (!_token.last_name ? "" : _token.last_name);

    /* ─── ANC Contact hook — all state & logic ─── */
    const ancContact = useANCContact(actions, patient_number, visit_id);

    /* ─── Snackbar ─── */
    const closeSnackbar = function() { actions.snackbarActions.hideSnackbar(); };

    /* ─── Navigate to ANC booking ─── */
    const goToBooking = function() {
        history.push(
            "/gyn-and-obs-antenatal-card/" + patient_number + "/" + visit_id + "/" + patient_type
        );
    };

    /* ─── Format EDD ─── */
    const formatEDD = function(edd) {
        if (!edd) return "—";
        try {
            return new Date(edd).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
            });
        } catch (e) {
            return edd;
        }
    };

    return (
        <div className="journals">
            <PageTitle title="ANC Clinical Assessment" />
            <SubHeader
                title="OB/GYN"
                subTitle="ANC Clinical Assessment"
                doctor_name={doctor_name}
            >
                <div className="header-icon-margin-right-zero">
                    <img
                        src="/images/antenatal-blue.png"
                        className="icon-resize surgery-icon-tabs ante-natal-green"
                        alt="ANC"
                    />
                </div>
            </SubHeader>
            <MainSnackbar
                variant={snackbar.type}
                open={snackbar.openBar}
                message={snackbar.message}
                handleCloseBar={closeSnackbar}
            />

            <ObGynSidebar
                patient_number={patient_number}
                visit_id={visit_id}
                patient_type={patient_type}
            >
                <ObGynPatientInfo
                    patient_number={patient_number}
                    visit_id={visit_id}
                />

                <AssessmentContainer>
                    {/* ═══ LOADING STATE ═══ */}
                    {ancContact.loadingBooking ? (
                        <div style={{ marginTop: "16px" }}>
                            {/* Skeleton header */}
                            <div
                                style={{
                                    background: "linear-gradient(135deg, #3871B7 0%, #5a8fd4 100%)",
                                    borderRadius: "12px",
                                    padding: "20px",
                                    marginBottom: "16px",
                                }}
                            >
                                <SkeletonLine width="200px" height="20px" />
                                <div style={{ height: "10px" }} />
                                <div style={{ display: "flex", gap: "20px" }}>
                                    <SkeletonLine width="80px" height="30px" />
                                    <SkeletonLine width="100px" height="30px" />
                                    <SkeletonLine width="80px" height="30px" />
                                </div>
                                <div style={{ height: "12px" }} />
                                <SkeletonLine width="100%" height="8px" />
                            </div>
                            {/* Skeleton tabs */}
                            <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
                                <SkeletonLine width="140px" height="40px" />
                                <SkeletonLine width="140px" height="40px" />
                            </div>
                            {/* Skeleton form card */}
                            <div
                                style={{
                                    background: "#fff",
                                    border: "1px solid #e4e5e9",
                                    borderRadius: "10px",
                                    padding: "20px",
                                }}
                            >
                                <SkeletonLine width="60%" height="18px" />
                                <div style={{ height: "14px" }} />
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(4,1fr)",
                                        gap: "16px",
                                    }}
                                >
                                    {[1, 2, 3, 4].map(function(i) {
                                        return (
                                            <div key={i}>
                                                <SkeletonLine width="50px" height="10px" />
                                                <div style={{ height: "6px" }} />
                                                <SkeletonLine width="100%" height="34px" />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* ═══ NO BOOKING STATE ═══ */}
                    {!ancContact.loadingBooking && !ancContact.bookingData ? (
                        <div style={{ marginTop: "16px" }}>
                            <NoBookingCard>
                                <NoBookingIcon>📋</NoBookingIcon>
                                <NoBookingTitle>
                                    No Active ANC Booking Found
                                </NoBookingTitle>
                                <NoBookingDesc>
                                    This patient must be registered in the Antenatal Care
                                    program before clinical assessments can be recorded.
                                </NoBookingDesc>
                                <NoBookingButton onClick={goToBooking}>
                                    Go to ANC Registration &amp; Booking
                                </NoBookingButton>
                            </NoBookingCard>
                        </div>
                    ) : null}

                    {/* ═══ MAIN CONTENT (when booking exists) ═══ */}
                    {!ancContact.loadingBooking && ancContact.bookingData ? (
                        <div style={{ marginTop: "16px" }}>
                           

                            {/* ─── View Toggle ─── */}
                            <ObGynTabBar>
                                <ObGynTabBtn
                                    active={ancContact.activeView === "form"}
                                    onClick={function() { ancContact.setActiveView("form"); }}
                                >
                                    Record Contact
                                </ObGynTabBtn>
                                <ObGynTabBtn
                                    active={
                                        ancContact.activeView === "timeline" ||
                                        ancContact.activeView === "detail"
                                    }
                                    onClick={function() { ancContact.setActiveView("timeline"); }}
                                >
                                    Contact History
                                    {ancContact.contacts.length > 0 ? (
                                        <ObGynTabBadge
                                            active={
                                                ancContact.activeView === "timeline" ||
                                                ancContact.activeView === "detail"
                                            }
                                        >
                                            {ancContact.contacts.length}
                                        </ObGynTabBadge>
                                    ) : null}
                                </ObGynTabBtn>
                            </ObGynTabBar>

                            {/* ─── Form View ─── */}
                            {ancContact.activeView === "form" ? (
                                <ANCContactForm ancContact={ancContact} />
                            ) : null}

                            {/* ─── Timeline View ─── */}
                            {(ancContact.activeView === "timeline" ||
                                ancContact.activeView === "detail") ? (
                                <ANCContactTimeline
                                    contacts={ancContact.contacts}
                                    loadingContacts={ancContact.loadingContacts}
                                    bookingData={ancContact.bookingData}
                                    selectedContact={ancContact.selectedContact}
                                    viewContact={ancContact.viewContact}
                                    closeContactDetail={ancContact.closeContactDetail}
                                />
                            ) : null}
                        </div>
                    ) : null}
                </AssessmentContainer>
            </ObGynSidebar>
        </div>
    );
};

function mapStateToProps(state) {
    return {
        snackbar: state.snackbar,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: {
            snackbarActions: bindActionCreators(snackbarActions, dispatch),
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ANCClinicalAssessment);
